import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { ErrorMessage } from 'foundations-components/transitional/components/ErrorMessage';
import { Panel } from 'foundations-components/transitional/components/Panel';
import { PanelSection } from 'foundations-components/transitional/components/Panel';
import { PanelBody } from 'foundations-components/transitional/components/PanelBody';
import { PanelHeader } from 'foundations-components/transitional/components/PanelHeader';
import HR from 'UIComponents/elements/HR';
import ForecastTypeDescription from 'ui-deal-management-components-lib/components/forecastTypeDescription/ForecastTypeDescription';
import { DealSidebarFooter } from './DealSidebarFooter';
import { DealSidebarOverview } from './DealSidebarOverview';
import { DealSidebarTabs } from './DealSidebarTabs';
import { fetchDeals as fetchDealsAction } from '../../actions/DealsActions';
import { setDealSidebarOwnerId as setDealSidebarOwnerIdAction } from '../../actions/OverviewDataActions';
import { DEALS_TAB, GOALS_TAB } from '../../constants/OverviewConstants';
import { SUCCEEDED } from '../../constants/RequestStatusTypes';
import { getIsUngatedToForecastingMultipleOutcomes } from '../../selectors/authSelectors';
import { getRowForId, getHasAllPipelinesGoal } from '../../selectors/aggregatedOverviewDataSelectors';
import { getDealsFetchError, getDealsLoading } from '../../selectors/dealsSelectors';
import { getFilters, getPipelineFilter, getTimePeriodFilter } from '../../selectors/filtersSelectors';
import { getIsAllPipelines, getIsDealStageView, getSelectedForecastType } from '../../selectors/forecastSelectors';
import { getOwnerIdToUserIdMap, getOwnersMap } from '../../selectors/ownersSelectors';
import { getDealSidebarIsForTeam, getDefaultDealSidebarTab } from '../../selectors/overviewSelectors';
import { getFiscalOffset } from '../../selectors/portalSettings';
import { getProperties, getPropertiesStatus } from '../../selectors/properties';
import { isMissingCurrencyCode } from '../../utils/currency';
import { getForecastedAmount } from '../../utils/submissionUtils';
import { trackSwitchToDealsSidebar, trackSwitchToGoalsSidebar } from '../../utils/usageTracker';
import { useFetchSpecificPipelineStages } from '../../hooks/useFetchSpecificPipelineStages';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DealSidebarContainer = () => {
  var _useSelector;
  const dispatch = useDispatch();
  const areDealsLoading = useSelector(getDealsLoading);
  const defaultTab = useSelector(getDefaultDealSidebarTab);
  const filterState = useSelector(getFilters);
  const fiscalOffset = (_useSelector = useSelector(getFiscalOffset)) !== null && _useSelector !== void 0 ? _useSelector : 0;
  const hasAllPipelinesGoal = useSelector(getHasAllPipelinesGoal);
  const hasDealsFetchError = useSelector(getDealsFetchError);
  const isAllPipelinesSelected = useSelector(getIsAllPipelines);
  const isDealStageView = useSelector(getIsDealStageView);
  const isTeam = useSelector(getDealSidebarIsForTeam);
  const isUngatedToForecastingMultipleOutcomes = useSelector(getIsUngatedToForecastingMultipleOutcomes);
  const owners = useSelector(getOwnersMap);
  const ownerIdToUserIdMap = useSelector(getOwnerIdToUserIdMap);
  const properties = useSelector(getProperties);
  const propertiesStatus = useSelector(getPropertiesStatus);
  const selectedForecastType = useSelector(getSelectedForecastType);
  const selectedPipelines = useSelector(getPipelineFilter);
  const sidebarRowData = useSelector(getRowForId);
  const timePeriodFilter = useSelector(getTimePeriodFilter);
  const [selectedTab, setSelectedTab] = useState(defaultTab);
  const {
    data: dealStages,
    loading: dealStagesLoading,
    error: dealStagesError
  } = useFetchSpecificPipelineStages(selectedPipelines.first());
  const {
    assigneeType,
    closed,
    goal,
    id,
    isUser,
    name
  } = sidebarRowData;
  useEffect(() => {
    dispatch(fetchDealsAction(`${id}`)).catch(() => {
      // should be caught in fetchDealData
    });
    setSelectedTab(defaultTab);
  }, [defaultTab, dispatch, id]);
  const handleSelectedChange = useCallback(tabId => {
    if (tabId === DEALS_TAB) {
      trackSwitchToDealsSidebar({
        isDealStageView
      });
    } else if (tabId === GOALS_TAB) {
      trackSwitchToGoalsSidebar({
        isDealStageView
      });
    }
    setSelectedTab(tabId);
  }, [isDealStageView]);
  const handleClose = useCallback(() => dispatch(setDealSidebarOwnerIdAction({
    dealSidebarId: null,
    isTeam: !isUser
  })), [dispatch, isUser]);

  // Goals with a value of `0` are a default for "no goal".
  // It's sensical for these to not have valid currency codes.
  // It is not sensical for there to be no currency code and
  // a goal value.
  const isEmptyCurrencyWithValue = isMissingCurrencyCode(goal) && !!goal.value;
  const isLoading = !(propertiesStatus === SUCCEEDED) || dealStagesLoading || !!dealStagesError || areDealsLoading;
  const owner = owners.get(ownerIdToUserIdMap.get(id));
  const DealsSidebarHeader = () => /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(PanelHeader, {
      children: /*#__PURE__*/_jsx(Text, {
        as: "h2",
        color: "text-primary-on-fill-default",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "dealSidebar.header"
        })
      })
    }), /*#__PURE__*/_jsx(DialogCloseButton, {
      onClick: handleClose
    })]
  });
  if (hasDealsFetchError || dealStagesError) {
    return /*#__PURE__*/_jsxs(Panel, {
      width: 525,
      children: [/*#__PURE__*/_jsx(DealsSidebarHeader, {}), /*#__PURE__*/_jsx(PanelBody, {
        children: /*#__PURE__*/_jsx(ErrorMessage, {
          type: "badRequest",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "dealSidebar.blockingError"
          })
        })
      })]
    });
  }
  return /*#__PURE__*/_jsxs(Panel, {
    "data-test-id": "deal-sidebar",
    width: 525,
    children: [/*#__PURE__*/_jsx(DealsSidebarHeader, {}), /*#__PURE__*/_jsxs(PanelBody, {
      display: "flex",
      flexDirection: "column",
      flexGrow: 1,
      children: [/*#__PURE__*/_jsxs(PanelSection, {
        children: [isUngatedToForecastingMultipleOutcomes && /*#__PURE__*/_jsxs(Flex, {
          flexDirection: "column",
          marginBlockStart: "0",
          marginBlockEnd: "400",
          children: [/*#__PURE__*/_jsx(Text, {
            marginBlockEnd: "100",
            typography: "heading-400",
            children: selectedForecastType !== null && selectedForecastType !== void 0 && selectedForecastType.defaultType ? `${selectedForecastType.forecastName} ${I18n.text('forecastType.default')}` : selectedForecastType === null || selectedForecastType === void 0 ? void 0 : selectedForecastType.forecastName
          }), /*#__PURE__*/_jsx(ForecastTypeDescription, {
            properties: properties,
            forecastType: selectedForecastType
          })]
        }), !isEmptyCurrencyWithValue && /*#__PURE__*/_jsx(DealSidebarOverview, {
          assigneeType: assigneeType,
          closedWon: closed,
          fiscalOffset: fiscalOffset,
          forecastAmount: getForecastedAmount(sidebarRowData),
          goal: goal,
          hasAllPipelinesGoal: hasAllPipelinesGoal,
          isTeam: !!isTeam,
          isUngatedToForecastingMultipleOutcomes: isUngatedToForecastingMultipleOutcomes,
          pipelines: filterState.get('pipeline'),
          timePeriod: timePeriodFilter,
          userOrTeamData: {
            name,
            email: owner && owner.get('email'),
            isActive: owner && owner.get('isActive')
          }
        })]
      }), /*#__PURE__*/_jsx(HR, {
        distance: "small"
      }), /*#__PURE__*/_jsx(DealSidebarTabs, {
        assigneeType: assigneeType,
        dealStages: dealStages || [],
        selectedTab: selectedTab,
        id: id,
        isDealStageView: isDealStageView,
        isLoading: isLoading,
        isTeam: isTeam,
        onSelectedChange: handleSelectedChange
      })]
    }), !isLoading && /*#__PURE__*/_jsx(DealSidebarFooter, {
      selectedTab: selectedTab,
      isAllPipelinesSelected: isAllPipelinesSelected,
      selectedPipelines: selectedPipelines.toArray(),
      id: id,
      isTeam: isTeam,
      isDealStageView: isDealStageView
    })]
  });
};
export default DealSidebarContainer;